import { ActivityCharts } from '@/components/charts/activity-charts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { logger } from '@/lib/logger'
import { useDevicesStore } from '@/stores/devices-store'
import { useReportingStore } from '@/stores/reporting-store'
import { endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns'
import { de } from 'date-fns/locale'
import { CheckCircle, DollarSign, Phone, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type PeriodType = 'heute' | 'gestern' | 'zeitraum'

interface Statistics {
    callCount: number
    callsBusinessDone: number
    smsCount: number
    totalGuests: number
    avgCallDuration: number
}

export default function ActivityPage() {
    const { t } = useTranslation()
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('heute')
    const [customStartDate, setCustomStartDate] = useState<string>('')
    const [customEndDate, setCustomEndDate] = useState<string>('')
    const [startDate, setStartDate] = useState<Date>(startOfDay(new Date()))
    const [endDate, setEndDate] = useState<Date>(endOfDay(new Date()))
    const [isRefreshing, setIsRefreshing] = useState(false)

    // Store hooks
    const { callOverview, readReportingData, reset: resetReporting } = useReportingStore()
    const { currentDevice, isDeviceSelected } = useDevicesStore()

    // Initialize custom date range
    useEffect(() => {
        const today = new Date()
        const weekAgo = subDays(today, 7)
        setCustomStartDate(format(weekAgo, 'yyyy-MM-dd'))
        setCustomEndDate(format(today, 'yyyy-MM-dd'))
    }, [])

    // Statistics computation
    const statistics: Statistics = useMemo(() => {
        if (!callOverview || !callOverview.conversationsByState) {
            return {
                callCount: 0,
                callsBusinessDone: 0,
                smsCount: 0,
                totalGuests: 0,
                avgCallDuration: 0
            }
        }

        const conversationsByState = callOverview.conversationsByState
        const callsBusinessDone = conversationsByState.BUSINESSDATA_DONE?.length || 0
        const callCount = Object.values(conversationsByState).reduce(
            (sum: number, array: any[]) => sum + (Array.isArray(array) ? array.length : 0),
            0
        )

        const totalGuests = callsBusinessDone > 0 ? Math.round(callsBusinessDone * 2.3) : 0
        const smsCount = callOverview.smsCount || 0
        const avgCallDuration = callOverview.averageCallDuration || 0

        return {
            callCount: Math.max(0, Number(callCount) || 0),
            callsBusinessDone: Math.max(0, callsBusinessDone),
            smsCount: Math.max(0, smsCount),
            totalGuests: Math.max(0, totalGuests),
            avgCallDuration: Math.max(0, avgCallDuration)
        }
    }, [callOverview])

    // Cost calculations - TODO: These should come from device configuration
    const avgTimePerCallMinutes = Number(currentDevice?.deviceConfiguration?.avgCallTimeMinutes) || 3
    const hourlyRate = Number(currentDevice?.deviceConfiguration?.hourlyRate) || 15

    const savedHours = useMemo(() => {
        const totalMinutes = statistics.callCount * avgTimePerCallMinutes
        return Math.max(0, totalMinutes / 60)
    }, [statistics.callCount, avgTimePerCallMinutes])

    const savedMoney = useMemo(() => {
        return Math.max(0, savedHours * hourlyRate)
    }, [savedHours, hourlyRate])

    // Booking percentage calculation
    const bookingPercentage = useMemo(() => {
        if (!statistics.callCount || statistics.callCount === 0) return 0
        const percentage = (statistics.callsBusinessDone / statistics.callCount) * 100
        return Math.min(100, Math.max(0, Math.round(percentage)))
    }, [statistics.callCount, statistics.callsBusinessDone])

    // Period text computation
    const periodText = useMemo(() => {
        if (selectedPeriod === 'heute') {
            return t('activityPage.today')
        } else if (selectedPeriod === 'gestern') {
            return t('activityPage.yesterday')
        } else {
            return `${format(startDate, 'dd.MM.yyyy', { locale: de })} - ${format(endDate, 'dd.MM.yyyy', { locale: de })}`
        }
    }, [selectedPeriod, startDate, endDate, t])

    // Load statistics function
    const loadStatistics = async () => {
        setIsRefreshing(true)
        try {
            if (isDeviceSelected()) {
                resetReporting()
                await readReportingData(
                    format(startDate, 'yyyy-MM-dd'),
                    format(endDate, 'yyyy-MM-dd')
                )
            }
        } catch (error) {
            console.error('Error loading statistics:', error)
        } finally {
            setIsRefreshing(false)
        }
    }

    // Period change handler
    const handlePeriodChange = async (period: PeriodType) => {
        setSelectedPeriod(period)

        if (period === 'heute') {
            setStartDate(startOfDay(new Date()))
            setEndDate(endOfDay(new Date()))
        } else if (period === 'gestern') {
            const yesterday = subDays(new Date(), 1)
            setStartDate(startOfDay(yesterday))
            setEndDate(endOfDay(yesterday))
        } else if (period === 'zeitraum') {
            if (customStartDate && customEndDate) {
                setStartDate(startOfDay(parseISO(customStartDate)))
                setEndDate(endOfDay(parseISO(customEndDate)))
            }
        }

        await loadStatistics()
    }

    // Custom date range handler
    const handleCustomDateChange = async () => {
        if (selectedPeriod === 'zeitraum' && customStartDate && customEndDate) {
            setStartDate(startOfDay(parseISO(customStartDate)))
            setEndDate(endOfDay(parseISO(customEndDate)))
            await loadStatistics()
        }
    }

    // Format number helper
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('de-DE').format(num)
    }

    // Load data on mount  
    const loadReportingData = async () => {
        if (!currentDevice) {
            logger.debug('Activity Page: No device selected yet, waiting...')
            return;
        }

        logger.info('Activity Page: Loading statistics', {
            deviceName: currentDevice.name,
            deviceId: currentDevice.deviceId
        })
        await loadStatistics()
    }

    useEffect(() => {
        if (currentDevice) {
            loadReportingData()
        }
    }, [currentDevice])

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">
                                {currentDevice?.name || t('navigation.activity')}
                            </h1>
                            <p className="text-sm text-gray-600 mt-1">{periodText}</p>
                        </div>

                        {/* Period Selection */}
                        <div className="flex bg-gray-100 rounded-md p-1">
                            <Button
                                onClick={() => handlePeriodChange('heute')}
                                variant={selectedPeriod === 'heute' ? 'default' : 'ghost'}
                                size="sm"
                            >
                                {t('activityPage.today')}
                            </Button>
                            <Button
                                onClick={() => handlePeriodChange('gestern')}
                                variant={selectedPeriod === 'gestern' ? 'default' : 'ghost'}
                                size="sm"
                            >
                                {t('activityPage.yesterday')}
                            </Button>
                            <Button
                                onClick={() => handlePeriodChange('zeitraum')}
                                variant={selectedPeriod === 'zeitraum' ? 'default' : 'ghost'}
                                size="sm"
                            >
                                {t('activityPage.period')}
                            </Button>
                        </div>
                    </div>

                    {/* Date Range */}
                    {selectedPeriod === 'zeitraum' && (
                        <div className="mt-4 pt-4 border-t flex gap-4">
                            <div>
                                <Label>{t('activityPage.from')}</Label>
                                <Input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    onBlur={handleCustomDateChange}
                                />
                            </div>
                            <div>
                                <Label>{t('activityPage.to')}</Label>
                                <Input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    onBlur={handleCustomDateChange}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {isRefreshing ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
                            <p className="text-gray-600">{t('loading.details')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {/* Bookings */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">{t('activityPage.bookings')}</p>
                                            <p className="text-2xl font-bold">
                                                {formatNumber(statistics.callsBusinessDone || 0)}
                                            </p>
                                        </div>
                                        <CheckCircle className="h-6 w-6 text-green-600" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {bookingPercentage}% {t('activityPage.successRate')}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Guests */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">{t('activityPage.guests')}</p>
                                            <p className="text-2xl font-bold">
                                                {formatNumber(Math.round(statistics.totalGuests || statistics.callsBusinessDone * 2.3 || 0))}
                                            </p>
                                        </div>
                                        <Users className="h-6 w-6 text-blue-600" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Ø {((statistics.totalGuests || statistics.callsBusinessDone * 2.3) / (statistics.callsBusinessDone || 1)).toFixed(1)} {t('activityPage.perBooking')}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Calls */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">{t('navigation.calls')}</p>
                                            <p className="text-2xl font-bold">
                                                {formatNumber(statistics.callCount || 0)}
                                            </p>
                                        </div>
                                        <Phone className="h-6 w-6 text-purple-600" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {statistics.avgCallDuration > 0 ? `${Math.round(statistics.avgCallDuration)}s` : '3min'} Ø
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Savings */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">{t('activityPage.saved')}</p>
                                            <p className="text-2xl font-bold">
                                                {formatNumber(Math.round(savedMoney || 0))}€
                                            </p>
                                        </div>
                                        <DollarSign className="h-6 w-6 text-orange-600" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {Math.round(savedHours || 0)}h
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Analytics</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ActivityCharts />
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </div>
    )
}